/**
 * NL-027 — the active "coach-edit session".
 *
 * When a coach opens a client's tool form via the macOS "Edit results"
 * launcher, the URL carries `?coachEdit=<jwt>`. `useToolRouteEffects`
 * (mounted in AppLayout) mirrors that here for the lifetime of the tool
 * route; `submitToolCompletion` consults it and, when set, routes the save
 * to the coach-edit endpoint (`PUT …/completions/coach-edit`).
 *
 * The session is **scoped to the exact (clientId, toolKey) it was minted
 * for** — not a bare global token. A coach-edit JWT is issued for one
 * specific client + tool, so a save for any *other* (client, tool) must NOT
 * pick it up. Without this scoping, a save that races a navigation (e.g. a
 * deferred submit firing as the route changes) could route to the coach-edit
 * endpoint with a token the server then rejects — a spurious save failure.
 * The `toolKey` is the canonical completion key (the F-022
 * `kpi-pre-assessment` alias is normalised to `discovery-call` upstream), so
 * it matches what `submitToolCompletion` actually sends.
 *
 * A module singleton (not React context) because the consumer is a plain
 * api-client function and only one tool route is ever active at a time.
 */
export interface CoachEditSession {
  token: string;
  clientId: string;
  /** Canonical completion key (what `submitToolCompletion` saves under). */
  toolKey: string;
}

let activeSession: CoachEditSession | null = null;

/** Arm (or clear, with `null`) the active coach-edit session. A blank token
 * clears, so a stray `?coachEdit=` never half-activates. */
export function setActiveCoachEdit(session: CoachEditSession | null): void {
  activeSession = session && session.token.length > 0 ? session : null;
}

/**
 * The coach-edit token to use for a save targeting `(clientId, toolKey)`, or
 * `null` when no coach-edit session is open for that exact pair.
 */
export function getActiveCoachEditToken(clientId: string, toolKey: string): string | null {
  if (!activeSession) return null;
  return activeSession.clientId === clientId && activeSession.toolKey === toolKey
    ? activeSession.token
    : null;
}
